/**
 * PortfolioTrendsPanel — Feb 2026 · R138w
 *
 * Recharts sparklines on top of the Executive Portfolio showing 8-week
 * (default) trends of the four velocity levers directors care about:
 *   - Tasks completed
 *   - Tasks created
 *   - Hours logged
 *   - Milestones completed
 *
 * Each mini-chart shows the sparkline plus the current week's value and
 * a delta vs the previous week (colour-coded up/down).
 */
import { useCallback, useEffect, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { TrendingUp, TrendingDown, Minus, CheckSquare, Plus, Clock, Flag } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const METRICS = [
  { key: "tasks_completed",     label: "Tasks completed",     Icon: CheckSquare, color: "#10b981" },
  { key: "tasks_created",       label: "Tasks created",       Icon: Plus,        color: "#6366f1" },
  { key: "hours_logged",        label: "Hours logged",        Icon: Clock,       color: "#f59e0b" },
  { key: "milestones_completed", label: "Milestones done",    Icon: Flag,        color: "#0ea5e9" },
];

export default function PortfolioTrendsPanel({ portfolioId }) {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  // R138x · Feb 2026 — Directors can zoom the trend window between
  // 4w (sprint retro), 8w (default), 13w (quarter), 26w (H1/H2).
  const [range, setRange] = useState(() => {
    try { return Number(localStorage.getItem(`pf-trends-range:${portfolioId}`)) || 8; }
    catch { return 8; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portfolios/${portfolioId}/trends?weeks=${range}`);
      setWeeks(data.weeks || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load trends"));
    } finally { setLoading(false); }
  }, [portfolioId, range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    try { localStorage.setItem(`pf-trends-range:${portfolioId}`, String(range)); } catch { /* noop */ }
  }, [portfolioId, range]);

  const RANGES = [
    { v: 4,  label: "4w",  hint: "Sprint" },
    { v: 8,  label: "8w",  hint: "Default" },
    { v: 13, label: "13w", hint: "Quarter" },
    { v: 26, label: "26w", hint: "Half-year" },
  ];

  if (loading) {
    return <div className="border border-border p-4 text-sm text-muted-foreground" data-testid="portfolio-trends-loading">Loading trends…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 flex-wrap" data-testid="portfolio-trends-range">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2">Range</span>
        {RANGES.map((r) => (
          <button key={r.v}
                    type="button"
                    onClick={() => setRange(r.v)}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${range === r.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/40"}`}
                    title={r.hint}
                    data-testid={`portfolio-trends-range-${r.v}`}>
            {r.label}
          </button>
        ))}
      </div>
      {!weeks.length ? (
        <div className="border border-border p-4 text-sm text-muted-foreground" data-testid="portfolio-trends-empty">No trend data available.</div>
      ) : (
        <TrendsGrid weeks={weeks} />
      )}
    </div>
  );
}


function TrendsGrid({ weeks }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="portfolio-trends-panel">
      {METRICS.map((m) => {
        const values = weeks.map((w) => w[m.key] || 0);
        const current = values[values.length - 1] || 0;
        const prev = values[values.length - 2] || 0;
        const delta = current - prev;
        const total = values.reduce((s, v) => s + v, 0);
        const Icon = m.Icon;
        const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
        const deltaColor = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-muted-foreground";
        return (
          <div key={m.key} className="border border-border p-3 bg-card"
                 data-testid={`portfolio-trend-${m.key}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <Icon className="w-3 h-3" style={{ color: m.color }} />
                {m.label}
              </div>
              <div className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${deltaColor}`}>
                <DeltaIcon className="w-2.5 h-2.5" />
                {delta > 0 ? `+${delta}` : delta}
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold tabular-nums" data-testid={`portfolio-trend-${m.key}-current`}>
                {typeof current === "number" && !Number.isInteger(current) ? current.toFixed(1) : current}
              </div>
              <div className="text-[10px] text-muted-foreground">this week · {total} in {weeks.length}w</div>
            </div>
            <div className="h-10 -mx-1 mt-2" data-testid={`portfolio-trend-${m.key}-chart`}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weeks} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
                  <defs>
                    <linearGradient id={`grad-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={m.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={m.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="start_date" hide />
                  <Tooltip
                    contentStyle={{ fontSize: "11px", padding: "4px 8px", borderRadius: 0 }}
                    labelFormatter={(v) => `Week of ${v}`}
                    formatter={(v) => [v, m.label]}
                  />
                  <Area type="monotone" dataKey={m.key}
                            stroke={m.color} strokeWidth={2}
                            fill={`url(#grad-${m.key})`}
                            isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })}
    </div>
  );
}
